import React, { useState, useEffect, useCallback } from 'react';
import { FaEdit, FaPlus, FaTimes, FaCheckCircle, FaTrash, FaDoorClosed } from 'react-icons/fa';
import { apiCall } from '../../config/api';
import { Room, RoomType, Facility } from '../../types';
import { useAuth } from '../../contexts/AuthContext';

const FacilitiesRooms: React.FC = () => {
        const { token } = useAuth();
        const [rooms, setRooms] = useState<Room[]>([]);
        const [facilities, setFacilities] = useState<Facility[]>([]);
        const [roomTypes, setRoomTypes] = useState<RoomType[]>([]);
        const [selectedFacility, setSelectedFacility] = useState<string | number>('');
        const [selectedRoomType, setSelectedRoomType] = useState<string>('');
        const [loading, setLoading] = useState<boolean>(true);
        const [error, setError] = useState<string | null>(null);
        const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
        const [editingRoom, setEditingRoom] = useState<Room | null>(null);
        const [currentPage, setCurrentPage] = useState<number>(1);
        const [totalCount, setTotalCount] = useState<number>(0);
        const [hasNextPage, setHasNextPage] = useState<boolean>(false);
        const [hasPreviousPage, setHasPreviousPage] = useState<boolean>(false);
        const pageSize = 10;
        const [formData, setFormData] = useState({
                facility: '',
                room_type: '',
                name: '',
                number: '',
                floor: '',
                capacity: '',
                equipment_notes: '',
                is_active: true,
                is_bookable: true,
        });

        // Load facilities
        const loadFacilities = useCallback(async () => {
                try {
                        const response = await apiCall('/facilities/facilities/', { method: 'GET' }, token || undefined);
                        const data = await response.json();
                        const items = data.results || data;
                        setFacilities(Array.isArray(items) ? items : []);
                        if (items.length > 0 && !selectedFacility) {
                                setSelectedFacility(items[0].id);
                        }
                } catch (err) {
                        console.error('Error loading facilities:', err);
                }
        }, [token, selectedFacility]);

        // Load room types for selected facility
        const loadRoomTypes = useCallback(async () => {
                if (!selectedFacility) return;
                try {
                        const response = await apiCall(`/facilities/room-types/?facility=${selectedFacility}`, { method: 'GET' }, token || undefined);
                        const data = await response.json();
                        const items = data.results || data;
                        setRoomTypes(Array.isArray(items) ? items : []);
                } catch (err) {
                        console.error('Error loading room types:', err);
                }
        }, [token, selectedFacility]);

        // Load rooms
        const loadRooms = useCallback(async () => {
                if (!selectedFacility) return;
                try {
                        setLoading(true);
                        setError(null);
                        const roomTypeQuery = selectedRoomType ? `&room_type=${selectedRoomType}` : '';
                        const response = await apiCall(`/facilities/rooms/?facility=${selectedFacility}${roomTypeQuery}&page=${currentPage}&page_size=${pageSize}`, { method: 'GET' }, token || undefined);
                        const data = await response.json();
                        const isPaginated = data && typeof data === 'object' && Array.isArray(data.results);
                        const items = isPaginated ? data.results : data;
                        setRooms(Array.isArray(items) ? items : []);

                        if (isPaginated) {
                                setTotalCount(typeof data.count === 'number' ? data.count : (Array.isArray(items) ? items.length : 0));
                                setHasNextPage(Boolean(data.next));
                                setHasPreviousPage(Boolean(data.previous));
                        } else {
                                const count = Array.isArray(items) ? items.length : 0;
                                setTotalCount(count);
                                setHasNextPage(false);
                                setHasPreviousPage(false);
                        }
                } catch (err) {
                        console.error('Error loading rooms:', err);
                        setError(err instanceof Error ? err.message : 'Failed to load rooms');
                } finally {
                        setLoading(false);
                }
        }, [token, selectedFacility, selectedRoomType, currentPage]);

        useEffect(() => {
                loadFacilities();
        }, [loadFacilities]);

        useEffect(() => {
                if (selectedFacility) {
                        loadRoomTypes();
                        loadRooms();
                }
        }, [selectedFacility, selectedRoomType, currentPage, loadRoomTypes, loadRooms]);

        useEffect(() => {
                setCurrentPage(1);
                setSelectedRoomType('');
        }, [selectedFacility]);

        useEffect(() => {
                setCurrentPage(1);
        }, [selectedRoomType]);

        const handleAddRoom = () => {
                setEditingRoom(null);
                setFormData({
                        facility: selectedFacility?.toString() || '',
                        room_type: '',
                        name: '',
                        number: '',
                        floor: '',
                        capacity: '',
                        equipment_notes: '',
                        is_active: true,
                        is_bookable: true,
                });
                setIsModalOpen(true);
        };

        const handleEditRoom = async (room: Room) => {
                // Open immediately using available row data so edit is never blocked by detail-fetch failures
                setEditingRoom(room);
                setFormData({
                        facility: room.facility?.toString() || selectedFacility?.toString() || '',
                        room_type: room.room_type?.toString() || '',
                        name: room.name || '',
                        number: room.number || '',
                        floor: room.floor || '',
                        capacity: room.capacity?.toString() || '',
                        equipment_notes: room.equipment_notes || '',
                        is_active: room.is_active,
                        is_bookable: room.is_bookable,
                });
                setIsModalOpen(true);

                // Best-effort refresh with full details from API
                try {
                        const response = await apiCall(`/facilities/rooms/${room.id}/`, {
                                method: 'GET'
                        }, token || undefined);

                        if (!response.ok) {
                                return;
                        }

                        const fullRoom = await response.json();
                        setEditingRoom(fullRoom);
                        setFormData({
                                facility: fullRoom.facility?.toString() || room.facility?.toString() || selectedFacility?.toString() || '',
                                room_type: fullRoom.room_type?.toString() || room.room_type?.toString() || '',
                                name: fullRoom.name || room.name || '',
                                number: fullRoom.number || room.number || '',
                                floor: fullRoom.floor || room.floor || '',
                                capacity: fullRoom.capacity?.toString() || room.capacity?.toString() || '',
                                equipment_notes: fullRoom.equipment_notes || room.equipment_notes || '',
                                is_active: typeof fullRoom.is_active === 'boolean' ? fullRoom.is_active : room.is_active,
                                is_bookable: typeof fullRoom.is_bookable === 'boolean' ? fullRoom.is_bookable : room.is_bookable,
                        });
                } catch (err) {
                        console.error('Error loading room details (using row data fallback):', err);
                }
        };

        const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
                const target = e.target as HTMLInputElement;
                const { name, value, type, checked } = target;
                setFormData(prev => ({
                        ...prev,
                        [name]: type === 'checkbox' ? checked : value
                }));
        };

        const handleSubmit = async (e: React.FormEvent) => {
                e.preventDefault();
                if (!editingRoom && !formData.facility) {
                        setError('Please select a facility');
                        return;
                }
                try {
                        setError(null);
                        const endpoint = editingRoom
                                ? `/facilities/rooms/${editingRoom.id}/`
                                : '/facilities/rooms/';
                        
                        const method = editingRoom ? 'PATCH' : 'POST';
                        
                        const submitData = {
                                ...formData,
                                capacity: formData.capacity ? parseInt(formData.capacity) : null,
                        };
                        
                        await apiCall(endpoint, {
                                method,
                                body: JSON.stringify(submitData),
                        }, token || undefined);

                        setIsModalOpen(false);
                        setEditingRoom(null);
                        if (!editingRoom) {
                                setCurrentPage(1);
                        }
                        loadRooms();
                } catch (err) {
                        console.error('Error saving room:', err);
                        setError(err instanceof Error ? err.message : 'Failed to save room');
                }
        };

        const handleDeactivateRoom = async (id: number) => {
                if (!window.confirm('Are you sure you want to deactivate this room?')) {
                        return;
                }
                try {
                        setError(null);
                        await apiCall(`/facilities/rooms/${id}/`, {
                                method: 'PATCH',
                                body: JSON.stringify({ is_active: false }),
                        }, token || undefined);
                        loadRooms();
                } catch (err) {
                        console.error('Error deactivating room:', err);
                        setError(err instanceof Error ? err.message : 'Failed to deactivate room');
                }
        };

        const handleReactivateRoom = async (id: number) => {
                if (!window.confirm('Are you sure you want to reactivate this room?')) {
                        return;
                }
                try {
                        setError(null);
                        await apiCall(`/facilities/rooms/${id}/`, {
                                method: 'PATCH',
                                body: JSON.stringify({ is_active: true }),
                        }, token || undefined);
                        loadRooms();
                } catch (err) {
                        console.error('Error reactivating room:', err);
                        setError(err instanceof Error ? err.message : 'Failed to reactivate room');
                }
        };

        const showingStart = totalCount > 0 && rooms.length > 0 ? ((currentPage - 1) * pageSize) + 1 : 0;
        const showingEnd = totalCount > 0 && rooms.length > 0 ? Math.min(showingStart + rooms.length - 1, totalCount) : 0;

        return (
                <div className="p-6">
                        <div className="flex justify-between items-center mb-6">
                                <h1 className="text-2xl font-bold text-base-content flex items-center gap-3">
                                        <FaDoorClosed className="text-primary" />
                                        Rooms
                                </h1>
                                <button
                                        className="btn btn-primary"
                                        onClick={handleAddRoom}
                                        disabled={!selectedFacility || roomTypes.length === 0}
                                >
                                        <FaPlus className="mr-2" /> Add Room
                                </button>
                        </div>

                        <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                        <label className="label">
                                                <span className="label-text font-semibold">Select Facility</span>
                                        </label>
                                        <select
                                                className="select select-bordered w-full"
                                                value={selectedFacility}
                                                onChange={(e) => setSelectedFacility(e.target.value)}
                                        >
                                                {facilities.map((facility) => (
                                                        <option key={facility.id} value={facility.id}>
                                                                {facility.name}
                                                        </option>
                                                ))}
                                        </select>
                                        {roomTypes.length === 0 && selectedFacility && (
                                                <p className="text-warning text-sm mt-2">
                                                        No room types defined for this facility. Please create room types first.
                                                </p>
                                        )}
                                </div>

                                <div>
                                        <label className="label">
                                                <span className="label-text font-semibold">Filter by Room Type</span>
                                        </label>
                                        <select
                                                className="select select-bordered w-full"
                                                value={selectedRoomType}
                                                onChange={(e) => setSelectedRoomType(e.target.value)}
                                                disabled={!selectedFacility || roomTypes.length === 0}
                                        >
                                                <option value="">All room types</option>
                                                {roomTypes.map((type) => (
                                                        <option key={type.id} value={type.id}>
                                                                {type.name}
                                                        </option>
                                                ))}
                                        </select>
                                </div>
                        </div>

                        {error && (
                                <div className="alert alert-error mb-4">
                                        <span>{error}</span>
                                </div>
                        )}

                        {loading ? (
                                <div className="flex justify-center items-center py-12">
                                        <div className="loading loading-spinner loading-lg"></div>
                                </div>
                        ) : (
                                <div className="bg-base-100/50 rounded-box p-2 min-h-[520px] flex flex-col">
                                <div className="overflow-x-auto">
                                        <table className="table table-zebra table-sm">
                                                <thead>
                                                        <tr>
                                                                <th>Room Name</th>
                                                                <th>Number</th>
                                                                <th>Room Type</th>
                                                                <th>Floor</th>
                                                                <th>Capacity</th>
                                                                <th>Bookable</th>
                                                                <th>Status</th>
                                                                <th className="w-32">Actions</th>
                                                        </tr>
                                                </thead>
                                                <tbody>
                                                        {rooms.length === 0 ? (
                                                                <tr>
                                                                        <td colSpan={8} className="text-center py-8 text-base-content/60">
                                                                                No rooms found. Create your first room to get started.
                                                                        </td>
                                                                </tr>
                                                        ) : (
                                                                rooms.map((room) => (
                                                                        <tr key={room.id}>
                                                                                <td className="font-semibold">{room.name}</td>
                                                                                <td>{room.number || '-'}</td>
                                                                                <td>
                                                                                        <div className="flex items-center gap-2">
                                                                                                <span
                                                                                                        className="inline-block w-4 h-4 rounded border"
                                                                                                        style={{ backgroundColor: room.room_type_color }}
                                                                                                ></span>
                                                                                                {room.room_type_name}
                                                                                        </div>
                                                                                </td>
                                                                                <td>{room.floor || '-'}</td>
                                                                                <td>{room.capacity || '-'}</td>
                                                                                <td>
                                                                                        <span className={`badge ${room.is_bookable ? 'badge-success' : 'badge-warning'}`}>
                                                                                                {room.is_bookable ? 'Yes' : 'No'}
                                                                                        </span>
                                                                                </td>
                                                                                <td>
                                                                                        <span className={`badge ${room.is_active ? 'badge-success' : 'badge-error'}`}>
                                                                                                {room.is_active ? 'Active' : 'Inactive'}
                                                                                        </span>
                                                                                </td>
                                                                                <td>
                                                                                        <div className="flex gap-2">
                                                                                                <button
                                                                                                        className="btn btn-ghost btn-xs"
                                                                                                        onClick={() => handleEditRoom(room)}
                                                                                                        title="Edit"
                                                                                                >
                                                                                                        <FaEdit className="w-4 h-4" />
                                                                                                </button>
                                                                                                {room.is_active ? (
                                                                                                        <button
                                                                                                                className="btn btn-ghost btn-xs text-error"
                                                                                                                onClick={() => handleDeactivateRoom(room.id)}
                                                                                                                title="Deactivate"
                                                                                                        >
                                                                                                                <FaTrash className="w-4 h-4" />
                                                                                                        </button>
                                                                                                ) : (
                                                                                                        <button
                                                                                                                className="btn btn-ghost btn-xs text-success"
                                                                                                                onClick={() => handleReactivateRoom(room.id)}
                                                                                                                title="Reactivate"
                                                                                                        >
                                                                                                                <FaCheckCircle className="w-4 h-4" />
                                                                                                        </button>
                                                                                                )}
                                                                                        </div>
                                                                                </td>
                                                                        </tr>
                                                                ))
                                                        )}
                                                </tbody>
                                        </table>
                                </div>
                                <div className="mt-auto pt-4 flex items-center justify-end gap-3 min-h-[2.5rem]">
                                        {totalCount > 0 && (
                                                <>
                                                <div className="text-sm text-base-content/70">
                                                        Showing {showingStart}-{showingEnd} of {totalCount}
                                                </div>
                                                <div className="join">
                                                        <button
                                                                className="join-item btn btn-sm"
                                                                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                                                disabled={!hasPreviousPage || loading}
                                                        >
                                                                Previous
                                                        </button>
                                                        <button className="join-item btn btn-sm btn-disabled">
                                                                Page {currentPage}
                                                        </button>
                                                        <button
                                                                className="join-item btn btn-sm"
                                                                onClick={() => setCurrentPage((prev) => prev + 1)}
                                                                disabled={!hasNextPage || loading}
                                                        >
                                                                Next
                                                        </button>
                                                </div>
                                                </>
                                        )}
                                </div>
                                </div>
                        )}

                        {isModalOpen && (
                                <div className="modal modal-open">
                                        <div className="modal-box max-w-2xl">
                                                <div className="flex justify-between items-center mb-4">
                                                        <h3 className="font-bold text-lg">
                                                                {editingRoom ? 'Edit Room' : 'Create New Room'}
                                                        </h3>
                                                        <button
                                                                className="btn btn-ghost btn-sm btn-circle"
                                                                onClick={() => setIsModalOpen(false)}
                                                        >
                                                                <FaTimes />
                                                        </button>
                                                </div>

                                                <form onSubmit={handleSubmit}>
                                                        <div className="grid grid-cols-2 gap-4">
                                                                <div className="form-control col-span-2">
                                                                        <label className="label">
                                                                                <span className="label-text">Facility *</span>
                                                                        </label>
                                                                        <select
                                                                                name="facility"
                                                                                value={formData.facility}
                                                                                onChange={handleInputChange}
                                                                                className="select select-bordered"
                                                                                required
                                                                        >
                                                                                <option value="">Select a facility...</option>
                                                                                {facilities.map((facility) => (
                                                                                        <option key={facility.id} value={facility.id}>
                                                                                                {facility.name}
                                                                                        </option>
                                                                                ))}
                                                                        </select>
                                                                </div>

                                                                <div className="form-control col-span-2">
                                                                        <label className="label">
                                                                                <span className="label-text">Room Type *</span>
                                                                        </label>
                                                                        <select
                                                                                name="room_type"
                                                                                value={formData.room_type}
                                                                                onChange={handleInputChange}
                                                                                className="select select-bordered"
                                                                                required
                                                                        >
                                                                                <option value="">Select a room type...</option>
                                                                                {roomTypes.map((type) => (
                                                                                        <option key={type.id} value={type.id}>
                                                                                                {type.name}
                                                                                        </option>
                                                                                ))}
                                                                        </select>
                                                                </div>

                                                                <div className="form-control">
                                                                        <label className="label">
                                                                                <span className="label-text">Room Name *</span>
                                                                        </label>
                                                                        <input
                                                                                type="text"
                                                                                name="name"
                                                                                value={formData.name}
                                                                                onChange={handleInputChange}
                                                                                className="input input-bordered"
                                                                                placeholder="e.g., OR-1, Consulting Room A"
                                                                                required
                                                                        />
                                                                </div>

                                                                <div className="form-control">
                                                                        <label className="label">
                                                                                <span className="label-text">Room Number</span>
                                                                        </label>
                                                                        <input
                                                                                type="text"
                                                                                name="number"
                                                                                value={formData.number}
                                                                                onChange={handleInputChange}
                                                                                className="input input-bordered"
                                                                                placeholder="e.g., 201"
                                                                        />
                                                                </div>

                                                                <div className="form-control">
                                                                        <label className="label">
                                                                                <span className="label-text">Floor</span>
                                                                        </label>
                                                                        <input
                                                                                type="text"
                                                                                name="floor"
                                                                                value={formData.floor}
                                                                                onChange={handleInputChange}
                                                                                className="input input-bordered"
                                                                                placeholder="e.g., 2, Ground"
                                                                        />
                                                                </div>

                                                                <div className="form-control">
                                                                        <label className="label">
                                                                                <span className="label-text">Capacity</span>
                                                                        </label>
                                                                        <input
                                                                                type="number"
                                                                                name="capacity"
                                                                                value={formData.capacity}
                                                                                onChange={handleInputChange}
                                                                                className="input input-bordered"
                                                                                placeholder="Maximum occupancy"
                                                                                min="1"
                                                                        />
                                                                </div>

                                                                <div className="form-control col-span-2">
                                                                        <label className="label">
                                                                                <span className="label-text">Equipment Notes</span>
                                                                        </label>
                                                                        <textarea
                                                                                name="equipment_notes"
                                                                                value={formData.equipment_notes}
                                                                                onChange={handleInputChange}
                                                                                className="textarea textarea-bordered"
                                                                                placeholder="Available equipment and setup notes"
                                                                                rows={2}
                                                                        ></textarea>
                                                                </div>

                                                                <div className="form-control col-span-2">
                                                                        <div className="flex gap-6">
                                                                                <label className="label cursor-pointer gap-2">
                                                                                        <input
                                                                                                type="checkbox"
                                                                                                name="is_active"
                                                                                                checked={formData.is_active}
                                                                                                onChange={handleInputChange}
                                                                                                className="checkbox checkbox-primary"
                                                                                        />
                                                                                        <span className="label-text">Active</span>
                                                                                </label>
                                                                                <label className="label cursor-pointer gap-2">
                                                                                        <input
                                                                                                type="checkbox"
                                                                                                name="is_bookable"
                                                                                                checked={formData.is_bookable}
                                                                                                onChange={handleInputChange}
                                                                                                className="checkbox checkbox-primary"
                                                                                        />
                                                                                        <span className="label-text">Bookable</span>
                                                                                </label>
                                                                        </div>
                                                                </div>
                                                        </div>

                                                        <div className="modal-action">
                                                                <button
                                                                        type="button"
                                                                        className="btn btn-ghost"
                                                                        onClick={() => setIsModalOpen(false)}
                                                                >
                                                                        Cancel
                                                                </button>
                                                                <button type="submit" className="btn btn-primary">
                                                                        {editingRoom ? 'Update Room' : 'Create Room'}
                                                                </button>
                                                        </div>
                                                </form>
                                        </div>
                                </div>
                        )}
                </div>
        );
};

export default FacilitiesRooms;
